import React, { useMemo } from 'react';
import { Options, OptionsOrGroups, SingleValue } from 'react-select';
import { CreatableReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import * as MeasureDisplayFormatUI from './MeasureDisplayFormatUI';
import { BasicSingleValue } from '../../Dropdown/StyledReactSelect/SelectUtils';
import {
  ICreatableReactSelectOption,
  IReactSelectOption,
} from '../../../utils/types';

interface IProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

const MAX_FIELD_VALUE_LENGTH = 9;

const defaultCalculationValueOptions: IReactSelectOption[] = [
  {
    label: '10',
    value: '10',
  },
  {
    label: '60',
    value: '60',
  },
  {
    label: '100',
    value: '100',
  },
  {
    label: '720',
    value: '720',
  },
  {
    label: '1000',
    value: '1000',
  },
  {
    label: '3600',
    value: '3600',
  },
  {
    label: '86400',
    value: '86400',
  },
  {
    label: '1000000',
    value: '1000000',
  },
];

const MeasureDisplayFormatCalculationValue = ({ value, onChange }: IProps) => {
  const handleChangeCalculationValue = (
    option: SingleValue<ICreatableReactSelectOption>,
  ) => {
    onChange(option?.value ?? null);
  };

  const selectedValue: ICreatableReactSelectOption | null = useMemo(() => {
    if (!value) {
      return null;
    }

    const foundOption = defaultCalculationValueOptions.find(
      (option) => option.value === value,
    );

    if (foundOption) {
      return foundOption;
    } else {
      return {
        label: value,
        value,
      };
    }
  }, [value]);

  const handleValidateNewOption = (
    inputValue: string,
    value: Options<IReactSelectOption>,
    options: OptionsOrGroups<IReactSelectOption, never>,
  ) => {
    if (!inputValue?.length) {
      return false;
    }

    // We allow users to put their own value,
    // that's why validating it to be a valid number
    // drop zero and drop exponential
    if (
      Number.isNaN(+inputValue) ||
      Number(inputValue) === 0 ||
      inputValue.includes('e') ||
      inputValue.includes('Infinity')
    ) {
      return false;
    }

    // Check length
    if (inputValue.length > MAX_FIELD_VALUE_LENGTH) {
      return false;
    }

    // Check menu value
    if (
      value.some(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    // Check options
    if (
      options?.length &&
      options.some(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    return true;
  };

  return (
    <MeasureDisplayFormatUI.CalculationValueMenu>
      <CreatableReactSelectMenu
        value={selectedValue}
        onChange={handleChangeCalculationValue}
        isMulti={false}
        options={defaultCalculationValueOptions}
        components={{
          SingleValue: BasicSingleValue,
        }}
        styles={{
          control: (base) => ({
            ...base,
            width: '100%',
            minWidth: 'unset',
          }),
        }}
        // Create new option props:
        isValidNewOption={handleValidateNewOption}
        onCreateOption={onChange}
      />
    </MeasureDisplayFormatUI.CalculationValueMenu>
  );
};

export default MeasureDisplayFormatCalculationValue;
